import { useState } from 'react';

import { DropdownMenu } from '@/Components/filterbar/components/DropdownMenu';
import { ContentPaste } from '@mui/icons-material';
import { Box, Button, Icon, Stack, Typography } from '@mui/material';
import { toast } from 'react-toastify';

import { QrCode, useQrCodeRef } from '@/Components/QrCode';
import { getMediaItemUrl } from '@/Components/posts/utils';
import { useBreakpoints } from '@/Utility/hooks';

import ModalButton from '@/Components/inputs/ModalButton';
import { MediaItemSizeEnum } from '@/graphql/types';
import { useAppStore } from '@/inertia/AppStore';
import QrCodeIcon from '@mui/icons-material/QrCode';

type Props = {
  disabled?: boolean;
  isFallback?: boolean;
  buttonClassName?: string;
  buttonProps?: React.HTMLAttributes<HTMLButtonElement>;
};

export const QrCodeSharing = (props: Props) => {
  const { isMobile } = useBreakpoints();

  const [size, setSize] = useState<225 | 300>(isMobile ? 225 : 300);

  const qrRef = useQrCodeRef();
  const znglyInfo = useAppStore((state) => state?.znglyInfo);
  const isAppPreview = useAppStore((state) => state.isAppPreview);

  const qr = {
    primaryColor: znglyInfo?.qrPrimaryColor,
    secondaryColor: znglyInfo?.qrSecondaryColor,
    imageUrl: getMediaItemUrl(znglyInfo?.qrImage, MediaItemSizeEnum.Large),
  };

  const onCopy = async () => {
    const getData = async () => (await qrRef.current?.qrCode?.getRawData('png')) as Blob;

    try {
      await navigator.clipboard.write([
        new ClipboardItem({
          'image/png': getData(),
        }),
      ]);

      return toast.success('Copied to Clipboard');
    } catch (e) {
      return toast.error('Failed to copy to Clipboard');
    }
  };

  return (
    <ModalButton
      button={
        <button
          type="button"
          className={`qr-code ${props.buttonClassName ?? ''}`}
          aria-label="QR Code Sharing"
          {...props?.buttonProps}
        >
          <Icon component={QrCodeIcon} />
        </button>
      }
    >
      <Box>
        <Stack direction="column" alignItems="center" gap={1}>
          <Typography align="center" variant="h6">
            To share, use the <b>{`'Copy to Clipboard'`}</b> button below and then paste into an email or other socials.
          </Typography>
          <Stack direction="row" justifyContent="space-evenly">
            <DropdownMenu
              type="single-select"
              menuList={[
                !isMobile
                  ? {
                      name: '100%',
                      onClick: () => {
                        setSize(300);
                      },
                    }
                  : null,
                {
                  name: '75%',
                  onClick: () => {
                    setSize(225);
                  },
                },
              ].filter((i) => i !== null)}
              name={'size'}
            />
            <Button onClick={onCopy} className="z-btn-5" startIcon={<ContentPaste />} disabled={isAppPreview}>
              Copy To Clipboard
            </Button>
          </Stack>
          <QrCode
            ref={qrRef}
            cornersSquareOptions={{
              type: 'extra-rounded',
              color: qr?.primaryColor ?? undefined,
            }}
            cornersDotOptions={{
              color: qr?.secondaryColor ?? undefined,
            }}
            image={qr?.imageUrl ?? undefined}
            width={size}
            height={size}
            style={{
              height: 'unset',
              width: 'unset',
            }}
            data={isAppPreview ? 'Sorry. Cannot generate QR code in preview mode' : undefined}
          />
        </Stack>
      </Box>
    </ModalButton>
  );
};

export default QrCodeSharing;
